import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  Trophy, 
  Zap, 
  Timer, 
  TrendingUp, 
  TrendingDown,
  Star,
  Lock,
  ChevronRight,
  DollarSign,
  Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

// Mock sports data
const liveSports = [
  {
    id: 1,
    sport: 'Football',
    league: 'Premier League',
    homeTeam: 'Manchester United',
    awayTeam: 'Liverpool FC',
    homeOdds: 2.45,
    drawOdds: 3.20,
    awayOdds: 2.80,
    time: '65\'',
    homeScore: 1,
    awayScore: 1,
    isLive: true
  },
  {
    id: 2,
    sport: 'Football',
    league: 'La Liga',
    homeTeam: 'Real Madrid',
    awayTeam: 'Barcelona',
    homeOdds: 2.10,
    drawOdds: 3.40,
    awayOdds: 3.25,
    time: '32\'',
    homeScore: 2,
    awayScore: 0,
    isLive: true
  },
  {
    id: 3,
    sport: 'Basketball',
    league: 'NBA',
    homeTeam: 'LA Lakers',
    awayTeam: 'Boston Celtics',
    homeOdds: 1.85,
    drawOdds: null,
    awayOdds: 1.95,
    time: 'Q3 8:24',
    homeScore: 78,
    awayScore: 82,
    isLive: true
  }
];

const upcomingMatches = [
  {
    id: 4,
    sport: 'Football',
    league: 'Champions League',
    homeTeam: 'Bayern Munich',
    awayTeam: 'PSG',
    homeOdds: 1.90,
    drawOdds: 3.60,
    awayOdds: 3.80,
    startTime: 'Today, 21:00',
    isLive: false
  },
  {
    id: 5,
    sport: 'Tennis',
    league: 'ATP Finals',
    homeTeam: 'Djokovic N.',
    awayTeam: 'Alcaraz C.',
    homeOdds: 1.75,
    drawOdds: null,
    awayOdds: 2.05,
    startTime: 'Today, 19:30',
    isLive: false
  },
  {
    id: 6,
    sport: 'Rugby',
    league: 'Super Rugby',
    homeTeam: 'Stormers',
    awayTeam: 'Bulls',
    homeOdds: 2.20,
    drawOdds: 15.00,
    awayOdds: 1.65,
    startTime: 'Tomorrow, 15:00',
    isLive: false
  },
  {
    id: 7,
    sport: 'Cricket',
    league: 'IPL',
    homeTeam: 'Mumbai Indians',
    awayTeam: 'Chennai Super Kings',
    homeOdds: 1.95,
    drawOdds: null,
    awayOdds: 1.85,
    startTime: 'Tomorrow, 19:30',
    isLive: false
  }
];

const popularBets = [
  { id: 1, name: 'Man United to win', odds: 2.45, type: 'Match Winner' },
  { id: 2, name: 'Over 2.5 Goals', odds: 1.85, type: 'Total Goals' },
  { id: 3, name: 'Both Teams to Score', odds: 1.72, type: 'BTTS' },
  { id: 4, name: 'First Half Draw', odds: 2.10, type: 'Half Time' }
];

export const BettingSection = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('live');
  const [betSlip, setBetSlip] = useState([]);

  const categories = [
    { id: 'live', name: 'Live Now', icon: Zap, count: 156 },
    { id: 'football', name: 'Football', icon: Trophy, count: 89 },
    { id: 'basketball', name: 'Basketball', icon: Target, count: 34 },
    { id: 'tennis', name: 'Tennis', icon: Star, count: 28 },
    { id: 'rugby', name: 'Rugby', icon: Trophy, count: 15 },
    { id: 'cricket', name: 'Cricket', icon: Trophy, count: 22 }
  ];

  const addToBetSlip = (match, selection, odds) => {
    const existing = betSlip.find(b => b.matchId === match.id);
    if (existing) {
      toast.error('Already in bet slip');
      return;
    }
    setBetSlip([...betSlip, {
      matchId: match.id,
      match: `${match.homeTeam} vs ${match.awayTeam}`,
      selection,
      odds
    }]);
    toast.success(`Added: ${selection} @ ${odds}`);
  };

  const removeBet = (matchId) => {
    setBetSlip(betSlip.filter(b => b.matchId !== matchId));
  };

  const totalOdds = betSlip.reduce((acc, bet) => acc * bet.odds, 1);

  const handlePlaceBet = () => {
    toast.info('Betting feature coming soon! This is a preview interface.');
    setBetSlip([]);
  };

  return (
    <div className="space-y-6" data-testid="betting-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-syne text-2xl font-bold text-gray-900">Sports Betting</h1>
        <div className="flex items-center gap-2 bg-green-100 px-3 py-1 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-green-700 text-sm font-medium">Live</span>
        </div>
      </div>

      {/* Preview Banner */}
      <Card className="card-raoul p-4 border-yellow-300 bg-yellow-50">
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="text-gray-900 font-medium">Preview Mode</p>
            <p className="text-gray-500 text-sm">This is a visual preview of the betting interface. Full functionality coming soon!</p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Categories */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-afriperx-green text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.name}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Live Matches */}
          <div>
            <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-afriperx-gold" />
              Live Matches
            </h2>
            <div className="space-y-4">
              {liveSports.map((match) => (
                <Card key={match.id} className="card-raoul p-4" data-testid={`match-${match.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{match.league}</span>
                      <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                        {match.time}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{match.sport}</span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-900 font-medium">{match.homeTeam}</span>
                        <span className="text-2xl font-bold text-gray-900">{match.homeScore}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-900 font-medium">{match.awayTeam}</span>
                        <span className="text-2xl font-bold text-gray-900">{match.awayScore}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => addToBetSlip(match, match.homeTeam, match.homeOdds)}
                      className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-center transition-colors"
                    >
                      <p className="text-xs text-gray-500">1</p>
                      <p className="text-gray-900 font-bold">{match.homeOdds}</p>
                    </button>
                    {match.drawOdds && (
                      <button
                        onClick={() => addToBetSlip(match, 'Draw', match.drawOdds)}
                        className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-center transition-colors"
                      >
                        <p className="text-xs text-gray-500">X</p>
                        <p className="text-gray-900 font-bold">{match.drawOdds}</p>
                      </button>
                    )}
                    <button
                      onClick={() => addToBetSlip(match, match.awayTeam, match.awayOdds)}
                      className={`p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-center transition-colors ${!match.drawOdds ? 'col-span-2' : ''}`}
                    >
                      <p className="text-xs text-gray-500">2</p>
                      <p className="text-gray-900 font-bold">{match.awayOdds}</p>
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Upcoming */}
          <div>
            <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-500" />
              Upcoming Events
            </h2>
            <div className="space-y-3">
              {upcomingMatches.map((match) => (
                <Card key={match.id} className="card-raoul p-4" data-testid={`upcoming-${match.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">{match.league}</span>
                    <span className="text-xs text-gray-400">{match.startTime}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium text-sm">{match.homeTeam} vs {match.awayTeam}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => addToBetSlip(match, match.homeTeam, match.homeOdds)}
                        className="px-3 py-1 bg-gray-50 hover:bg-gray-100 rounded text-sm font-medium text-gray-900"
                      >
                        {match.homeOdds}
                      </button>
                      {match.drawOdds && (
                        <button
                          onClick={() => addToBetSlip(match, 'Draw', match.drawOdds)}
                          className="px-3 py-1 bg-gray-50 hover:bg-gray-100 rounded text-sm font-medium text-gray-900"
                        >
                          {match.drawOdds}
                        </button>
                      )}
                      <button
                        onClick={() => addToBetSlip(match, match.awayTeam, match.awayOdds)}
                        className="px-3 py-1 bg-gray-50 hover:bg-gray-100 rounded text-sm font-medium text-gray-900"
                      >
                        {match.awayOdds}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Bet Slip Sidebar */}
        <div className="space-y-4">
          <Card className="card-raoul p-4 sticky top-4">
            <h3 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center justify-between">
              <span>Bet Slip</span>
              <span className="text-sm bg-afriperx-green text-white px-2 py-0.5 rounded-full">{betSlip.length}</span>
            </h3>
            
            {betSlip.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Your bet slip is empty</p>
                <p className="text-gray-400 text-xs">Click on odds to add selections</p>
              </div>
            ) : (
              <div className="space-y-3">
                {betSlip.map((bet) => (
                  <div key={bet.matchId} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-gray-900 font-medium text-sm">{bet.selection}</p>
                      <button
                        onClick={() => removeBet(bet.matchId)}
                        className="text-gray-400 hover:text-red-500 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs mb-2">{bet.match}</p>
                    <p className="text-afriperx-gold font-bold">{bet.odds}</p>
                  </div>
                ))}
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-500 text-sm">Total Odds</span>
                    <span className="text-gray-900 font-bold">{totalOdds.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-4">
                    <span className="text-gray-500 text-sm">Potential Win (R100)</span>
                    <span className="text-green-600 font-bold">R{(totalOdds * 100).toFixed(2)}</span>
                  </div>
                  
                  <Button
                    onClick={handlePlaceBet}
                    className="w-full btn-primary"
                    data-testid="place-bet-btn"
                  >
                    Place Bet
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Popular Bets */}
          <Card className="card-raoul p-4">
            <h3 className="font-syne text-md font-bold text-gray-900 mb-3">Popular Bets</h3>
            <div className="space-y-2">
              {popularBets.map((bet) => (
                <button
                  key={bet.id}
                  onClick={() => toast.info('Select from live matches above')}
                  className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-left"
                >
                  <div>
                    <p className="text-gray-900 text-sm font-medium">{bet.name}</p>
                    <p className="text-gray-400 text-xs">{bet.type}</p>
                  </div>
                  <span className="text-afriperx-gold font-bold">{bet.odds}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
